import { CartAddEvent } from '@theme/events';

const CART_ADD_PATH = /\/cart\/add(\.js)?(\?|$)/;

/**
 * Vitals wishlist and other apps POST to /cart/add.js without theme section HTML.
 * Bridge successful adds into the theme cart:update pipeline.
 */
function installExternalCartAddBridge() {
  const nativeFetch = window.fetch.bind(window);

  window.fetch = async (...args) => {
    const response = await nativeFetch(...args);

    const requestUrl = resolveRequestUrl(args[0]);
    const init = args[1];

    if (!CART_ADD_PATH.test(requestUrl) || !shouldBridgeRequest(init)) {
      return response;
    }

    try {
      const data = await response.clone().json();
      if (data?.status) return response;

      queueMicrotask(() => {
        void publishExternalCartAdd(data);
      });
    } catch {
      // Not JSON — leave to the caller.
    }

    return response;
  };
}

/**
 * @param {RequestInfo | URL} input
 * @returns {string}
 */
function resolveRequestUrl(input) {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  if (input instanceof Request) return input.url;
  return '';
}

/**
 * @param {RequestInit | undefined} init
 * @returns {boolean}
 */
function shouldBridgeRequest(init) {
  if (init?.body instanceof FormData) return false;

  const accept = getHeaderValue(init?.headers, 'Accept');
  if (accept.includes('text/html')) return false;

  return true;
}

/**
 * @param {HeadersInit | undefined} headers
 * @param {string} name
 * @returns {string}
 */
function getHeaderValue(headers, name) {
  if (!headers) return '';

  if (headers instanceof Headers) {
    return headers.get(name) ?? '';
  }

  if (Array.isArray(headers)) {
    const entry = headers.find(([key]) => key.toLowerCase() === name.toLowerCase());
    return entry?.[1] ?? '';
  }

  return String(headers[name] ?? headers[name.toLowerCase()] ?? '');
}

/**
 * @param {Record<string, unknown>} addResponse
 */
async function publishExternalCartAdd(addResponse) {
  const cartResponse = await fetch('/cart.js');
  if (!cartResponse.ok) return;

  const cart = await cartResponse.json();

  document.documentElement.dispatchEvent(
    new CartAddEvent(cart, 'external-cart-add', {
      source: 'external-cart-add',
      itemCount: cart.item_count,
      sections: /** @type {Record<string, string> | undefined} */ (addResponse.sections),
    })
  );
}

installExternalCartAddBridge();
